import { RenderPass } from "@/core/pipeline/render-pass"
import { colorWheelToRGBY, XYtoXYZ } from "@/core/utils/color-conversion"
import { whiteBalanceToXY } from "@/core/utils/temperature"
import {
	adjustPrimaries,
	getAdaptionMatrix,
	getWBGainData,
	primaries,
	primariesToMatrix,
} from "@/core/utils/color"
import { getCurvesData, isIdentityCurve } from "@/core/curves"
import { getHSLData } from "@/core/hsl"
import { getParametricCurvesData, isIdentityParametricCurve } from "@/core/parametric-curves"
import { getContrastCurve } from "@/core/tone-curve"
import { Texture } from "@/core/backends/webgl/texture"
import { Renderer } from "../renderer"
import { Adjustments, LookTable, LookTableType, MaskGroup } from "@/core/types"
import {
	getGlobalAdjustmentData,
	getLocalAdjustmentsData,
	GlobalAdjustmentsBufferSize,
	LocalAdjustmentsMaxCount,
	LocalParamsTextureWidth,
	LocalParamsTextureHeight,
} from "@/core/utils/adjustments-data"
import { fitToSize } from "@/core/utils/resize-image"
import { vec2 } from "gl-matrix"
import { DualBuffers } from "../dual-buffers"

export class AdjustmentsPass extends RenderPass {
	private globalAdjustmentBuffer: WebGLBuffer
	private wbGainCacheName: string
	private lutTexture: Texture
	private localAdjustmentsTexture: Texture // use texture to replace UBO to speed up shader creation

	constructor(renderer: Renderer, id: string) {
		super(renderer, id)
		const shaders = renderer.shaders

		this.globalAdjustmentBuffer = this.createBuffer(renderer)
		shaders.adjustments.initUniformBufferObject({
			name: "GlobalAdjustmentBlock",
			buffer: this.globalAdjustmentBuffer,
			dataSize: GlobalAdjustmentsBufferSize,
		})

		this.wbGainCacheName = ""

		this.lutTexture = renderer.backend.createTexture({
			label: "adjustments.lutTexture",
			width: 1,
			height: 1,
			format: "rgb32float",
		})

		this.localAdjustmentsTexture = renderer.backend.createTexture({
			label: "adjustments.localAdjustmentsTexture",
			width: LocalParamsTextureWidth,
			height: LocalParamsTextureHeight,
			format: "rgba32float",
		})
	}

	invalidateCache() {
		this.wbGainCacheName = ""
	}

	render(
		input: Texture,
		options: {
			outputTexture?: Texture
			adjustments: Adjustments
			shouldUseDefaultProfileAdjustments?: boolean
		},
	) {
		const { renderer } = this
		const { shaders, photo } = renderer
		const adjustments = options.adjustments

		this.renderer.performanceCounters.begin("renderAdjustments")

		if (this.hasNegativeSharpness(adjustments.localAdjustments)) {
			renderBlurTextures(renderer, input, renderer.blurBuffers)
		}

		// Render to target
		renderer.setRenderTarget(options.outputTexture)

		// White balance

		const asShotTemp = photo.whiteBalance.asShotTemperature
		const asShotTint = photo.whiteBalance.asShotTint

		const adjustedTint = adjustments.tint ?? asShotTint
		const adjustedTemp = adjustments.temperature ?? asShotTemp

		const asShotWhite = XYtoXYZ(whiteBalanceToXY([asShotTemp, asShotTint]))
		const adjustedWhite = XYtoXYZ(whiteBalanceToXY([adjustedTemp, adjustedTint]))
		const whiteBalanceMatrix = getAdaptionMatrix(adjustedWhite, asShotWhite)

		const hasLocalWB = this.hasLocalWhiteBalance(adjustments.localAdjustments)
		if (hasLocalWB) {
			const name = `${Math.round(asShotTemp)}|${Math.round(asShotTint)}|${Math.round(
				adjustedTemp,
			)}|${Math.round(adjustedTint)}`
			if (name !== this.wbGainCacheName) {
				const wbGainData = getWBGainData({
					adjustedTemp,
					adjustedTint,
					asShotWhite,
					adjustedWhite,
				})

				renderer.wbGainTexture.update({
					format: "rgb32float",
					data: wbGainData,
					width: 41,
					height: 41,
				})

				this.wbGainCacheName = name
			}
		}

		// Camera calibration

		const calibratedPrimaries = adjustPrimaries(primaries.proPhoto, {
			redHue: adjustments.calibrationRedHue,
			redSat: adjustments.calibrationRedSat,
			greenHue: adjustments.calibrationGreenHue,
			greenSat: adjustments.calibrationGreenSat,
			blueHue: adjustments.calibrationBlueHue,
			blueSat: adjustments.calibrationBlueSat,
		})

		const workingSpaceMatrix = primariesToMatrix(calibratedPrimaries)

		// Curves

		const curvesData = getCurvesData(adjustments)

		renderer.adjustmentsCurveTexture.update({
			format: "rgba32float",
			data: curvesData,
			width: curvesData.length / 4,
			height: 1,
		})

		const renderCurvesAll = !isIdentityCurve(adjustments.curvesAll as Array<[number, number]>)
		const renderCurvesRGB =
			!isIdentityCurve(adjustments.curvesRed as Array<[number, number]>) ||
			!isIdentityCurve(adjustments.curvesGreen as Array<[number, number]>) ||
			!isIdentityCurve(adjustments.curvesBlue as Array<[number, number]>)
		const renderParametricCurve = !isIdentityParametricCurve(adjustments)

		// HSL

		const hslData = getHSLData(adjustments)

		renderer.adjustmentsHslTexture.update({
			format: "rgb32float",
			data: hslData,
			width: 360,
			height: 1,
		})

		// LUT

		const userLut: LookTable = renderer.assets.getArtefact({
			type: "lut",
			id: adjustments.userLutId ?? "",
		})

		if (!options.shouldUseDefaultProfileAdjustments && userLut && userLut.data) {
			const width =
				userLut.type === LookTableType.rgb
					? userLut.satDivisions * userLut.satDivisions
					: userLut.satDivisions
			const height =
				userLut.type === LookTableType.rgb
					? userLut.hueDivisions
					: userLut.hueDivisions * userLut.valDivisions

			this.lutTexture.update({
				width,
				height,
				data: userLut.data,
			})
		} else {
			this.lutTexture.update({
				width: 1,
				height: 1,
				data: null,
			})
		}

		// Parametric Curve and Contrast Curve: combine in one texture to save a texture unit

		const parametricCurvesData = getParametricCurvesData(adjustments)

		const contrastCurve = getContrastCurve(adjustments.contrast, parametricCurvesData.length)

		const concatenatedData = new Float32Array(
			parametricCurvesData.length + contrastCurve.length,
		)
		for (let i = 0; i < parametricCurvesData.length; i++) {
			concatenatedData[2 * i] = parametricCurvesData[i] as number
			concatenatedData[2 * i + 1] = contrastCurve[i] as number
		}

		renderer.parametricAndContrastCurveTexture.update({
			format: "rg32float",
			data: concatenatedData,
			width: parametricCurvesData.length,
			height: 1,
		})

		// Grading

		const globalColor = colorWheelToRGBY([
			adjustments.gradingGlobalHue,
			adjustments.gradingGlobalSaturation,
			adjustments.gradingGlobalLuminance,
		])

		const shadowsColor = colorWheelToRGBY([
			adjustments.gradingShadowsHue,
			adjustments.gradingShadowsSaturation,
			adjustments.gradingShadowsLuminance,
		])

		const midtonesColor = colorWheelToRGBY([
			adjustments.gradingMidtonesHue,
			adjustments.gradingMidtonesSaturation,
			adjustments.gradingMidtonesLuminance,
		])

		const highlightsColor = colorWheelToRGBY([
			adjustments.gradingHighlightsHue,
			adjustments.gradingHighlightsSaturation,
			adjustments.gradingHighlightsLuminance,
		])

		const maskCount = Math.min(
			adjustments.localAdjustments?.length ?? 0,
			LocalAdjustmentsMaxCount,
		)

		// Global Adjustments buffer
		const globalAdjustmentData = getGlobalAdjustmentData(adjustments, {
			whiteBalanceMatrix,
			workingSpaceMatrix,
			globalColor,
			shadowsColor,
			midtonesColor,
			highlightsColor,
			imageSize: photo.imageState.croppedImageSize,
			adjustedTemp,
			adjustedTint,
			maskCount,
			renderLocalContrast: this.hasLocalContrast(adjustments.localAdjustments),
			renderLocalWB: hasLocalWB,
			userLut,
			exposureBias: photo.exposureBias,
		})

		renderer.backend.setUniformBufferData({
			buffer: this.globalAdjustmentBuffer,
			data: globalAdjustmentData,
		})

		// Profile uniforms
		const profileUniforms = renderer.profileManager.getUniforms(
			options.shouldUseDefaultProfileAdjustments ? photo.clone({ adjustments }) : photo,
		)

		// Local Adjustments Buffer
		if (maskCount > 0) {
			const localAdjustmentsData = getLocalAdjustmentsData(adjustments.localAdjustments)

			this.localAdjustmentsTexture.update({
				format: "rgba32float",
				data: localAdjustmentsData,
				width: LocalParamsTextureWidth,
				height: LocalParamsTextureHeight,
			})
		}

		input.bind(0)

		renderer.filteredTextures.meanA.bind(1)
		renderer.filteredTextures.meanB.bind(2)
		renderer.adjustmentsCurveTexture.bind(3)
		renderer.adjustmentsHslTexture.bind(4)
		renderer.parametricAndContrastCurveTexture.bind(5)
		renderer.grainTexture?.bind(6)

		if (this.lutTexture.width > 1 && this.lutTexture.height > 1) {
			this.lutTexture.bind(7)
		}

		this.localAdjustmentsTexture.bind(8)

		renderer.maskArray.bind(13)
		renderer.blurBuffers.readBuffer.bind(14)
		renderer.wbGainTexture.bind(15)

		shaders.adjustments.setUniforms({
			inputTexture: 0,
			meanA: 1,
			meanB: 2,
			adjustmentsCurveTexture: 3,
			adjustmentsHslTexture: 4,
			parametricAndContrastCurveTexture: 5,
			grainTexture: 6,
			userLutTexture: 7,

			localAdjustmentsTexture: 8,

			maskTextureArray: 13,
			blurredTexture: 14,
			wbGainTexture: 15,

			renderCurvesAll,
			renderCurvesRGB,
			renderParametricCurve,
		})

		if (profileUniforms) {
			if (adjustments.disableProfileLookTable) {
				profileUniforms.renderProfileLookTable = false
			}

			shaders.adjustments.setUniforms(profileUniforms)
		}

		shaders.adjustments.draw()

		this.renderer.performanceCounters.end("renderAdjustments")
	}

	hasNegativeSharpness(localAdjustments: MaskGroup[] | undefined) {
		if (!localAdjustments) return false

		for (const maskGroup of localAdjustments) {
			if (maskGroup.sharpen < 0) {
				return true
			}
		}

		return false
	}

	hasLocalContrast(localAdjustments: MaskGroup[] | undefined) {
		if (!localAdjustments) return false

		for (const maskGroup of localAdjustments) {
			if (maskGroup.contrast !== 0) {
				return true
			}
		}

		return false
	}

	hasLocalWhiteBalance(localAdjustments: MaskGroup[] | undefined) {
		if (!localAdjustments) return false

		for (const maskGroup of localAdjustments) {
			if (maskGroup.temperature !== 0 || maskGroup.tint !== 0) {
				return true
			}
		}

		return false
	}

	createBuffer(renderer: Renderer) {
		const buffer = renderer.backend.gl.createBuffer()

		if (!buffer) {
			throw new Error("Cannot create buffer for adjustments")
		}

		return buffer
	}

	dispose() {
		return
	}
}

function renderBlurTextures(renderer: Renderer, inputTexture: Texture, blurBuffers: DualBuffers) {
	const { shaders } = renderer
	const { width, height } = inputTexture

	let downscaledSize: vec2 = [width, height]
	let blurInput = inputTexture
	const targetDim = 1200
	const targetSize = fitToSize([width, height], [targetDim, targetDim])

	// 1. Downscale if needed

	const maxSide = Math.max(width, height)
	if (maxSide > targetDim) {
		downscaledSize = fitToSize([width, height], [targetDim, targetDim])
		blurBuffers.updateSize(downscaledSize[0], downscaledSize[1])

		renderer.setRenderTarget(blurBuffers.writeBuffer)

		inputTexture.bind(0)
		shaders.guidedDownsample.render({
			inputTexture: 0,
			offset: [(1 / downscaledSize[0]) * 0.7, (1 / downscaledSize[1]) * 0.7],
		})

		blurBuffers.swapBuffers()
		blurInput = blurBuffers.readBuffer
	}

	// 2. Fast Gaussian Blur with linear sampling
	blurBuffers.updateSize(downscaledSize[0], downscaledSize[1])

	// 2.1 horizontal pass
	renderer.setRenderTarget(blurBuffers.writeBuffer)
	blurInput.bind(0)
	shaders.gaussianBlur.render({
		inputTexture: 0,
		offset: [1 / targetSize[0], 0],
	})
	blurBuffers.swapBuffers()

	// 2.2 vertical pass
	renderer.setRenderTarget(blurBuffers.writeBuffer)
	blurBuffers.readBuffer.bind(0)
	shaders.gaussianBlur.render({
		inputTexture: 0,
		offset: [0, 1 / targetSize[1]],
	})
	blurBuffers.swapBuffers()
}
